import { ALL_MARKETPLACES, MARKETPLACE_TO_REALM, type Marketplace, type SymphonyRealmEnum } from '@amzn/journeys-common';
import { useEffect, useMemo, useState } from 'react';
import { useTPCampaignContext } from './TPCampaign/TPCampaignContext';
import { useActiveFlavors } from './useFlavor';

function useFirstFlavorRealm(): SymphonyRealmEnum | undefined {
  const activeFlavors = useActiveFlavors();
  const firstFlavorMarketplace = activeFlavors[0]?.scheduling.marketplace;

  return useMemo(() => {
    if (ALL_MARKETPLACES.includes(firstFlavorMarketplace as Marketplace)) {
      return MARKETPLACE_TO_REALM[firstFlavorMarketplace as Marketplace];
    }
    return;
  }, [
    firstFlavorMarketplace,
  ]);
}

function useEventRealm(defaultRealm: SymphonyRealmEnum | undefined): SymphonyRealmEnum | undefined {
  const { projectEvents } = useTPCampaignContext();
  // Symphony has different endpoints for Realms, users are not able to import Symphony events in multiple realms
  // All realms in projectEvents should match due to the above
  const projectEventRealm = projectEvents?.[0]?.realm ?? undefined;

  return useMemo(
    () =>
      // prevent using event realm after user makes manual selection
      defaultRealm === undefined ? (projectEventRealm as SymphonyRealmEnum | undefined) : undefined,
    [
      defaultRealm,
      projectEventRealm,
    ],
  );
}

export function useRealm(): {
  realm: SymphonyRealmEnum | undefined;
  setRealm: (realm: SymphonyRealmEnum | undefined) => void;
} {
  const defaultRealm = useFirstFlavorRealm();
  const eventRealm = useEventRealm(defaultRealm);

  const [realm, setRealm] = useState<SymphonyRealmEnum | undefined>(defaultRealm);

  useEffect(() => {
    if (eventRealm !== undefined) {
      setRealm(eventRealm);
    }
  }, [
    eventRealm,
  ]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code
  return useMemo(
    () => ({
      realm,
      setRealm,
    }),
    [
      realm,
      setRealm,
    ],
  );
}
